import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Mail, Lock, User, Loader2, Eye, EyeOff, Github, Sun, Moon } from '@/components/icons';
import { signIn, signUp, signInWithGitHub } from '../../lib/auth-client';
import { trackSignIn, trackSignUp } from '../../lib/posthog';
import { useAuth } from './AuthProvider';
import { useTheme } from '../../App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardFooter, CardHeader } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';
import StackSketchLogo from '/StackSketch-FullLogo.svg';

type AuthMode = 'signin' | 'signup';

export function AuthPage() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  const [searchParams] = useSearchParams();
  // Landing-page CTAs carry intent: mode=signup opens the Sign Up tab, and
  // plan=pro|lifetime routes into checkout once authenticated.
  const planParam = searchParams.get('plan');
  const [mode, setMode] = useState<AuthMode>(
    searchParams.get('mode') === 'signup' ? 'signup' : 'signin'
  );
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [isGitHubLoading, setIsGitHubLoading] = useState(false);

  // Redirect once authenticated — to pricing with the plan intent when the
  // visitor came from a paid CTA, otherwise to the dashboard.
  useEffect(() => {
    if (isAuthenticated) {
      navigate(planParam === 'pro' || planParam === 'lifetime' ? `/pricing?plan=${planParam}` : '/');
    }
  }, [isAuthenticated, navigate, planParam]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      if (mode === 'signin') {
        const result = await signIn.email({
          email,
          password,
        });
        if (result.error) {
          setError(result.error.message || 'Failed to sign in');
        } else {
          trackSignIn('email');
          resetForm();
          // Navigation will happen via useEffect when isAuthenticated becomes true
        }
      } else {
        const result = await signUp.email({
          email,
          password,
          name,
        });
        if (result.error) {
          setError(result.error.message || 'Failed to sign up');
        } else {
          trackSignUp('email');
          resetForm();
          // Navigation will happen via useEffect when isAuthenticated becomes true
        }
      }
    } catch (err) {
      setError('An unexpected error occurred');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const resetForm = () => {
    setEmail('');
    setPassword('');
    setName('');
    setError(null);
  };

  const switchMode = () => {
    setMode(mode === 'signin' ? 'signup' : 'signin');
    setError(null);
  };

  const handleGitHubSignIn = async () => {
    setIsGitHubLoading(true);
    setError(null);
    try {
      await signInWithGitHub();
      trackSignIn('github');
      trackSignUp('github');
      // Navigation will happen via useEffect when isAuthenticated becomes true
    } catch (err) {
      setError('Failed to sign in with GitHub');
      console.error(err);
      setIsGitHubLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-4 bg-background [background-image:radial-gradient(var(--border)_1px,transparent_1px)] [background-size:24px_24px]">
      {/* Logo and branding */}
      <div className="mb-8 text-center">
        <img src={StackSketchLogo} alt="Stack Sketch" className="w-32 h-auto mx-auto mb-4 dark:brightness-0 dark:invert" />
        <p className="text-muted-foreground">
          Create beautiful tech stack diagrams with AI assistance
        </p>
      </div>

      {/* Auth Card */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.2 }}
        className="w-full max-w-md"
      >
        <Card className="border-border shadow-xl">
          {/* Header */}
          <CardHeader className="border-b border-border">
            <h3 className="text-lg font-bold text-foreground">
              {mode === 'signin' ? 'Welcome Back' : 'Create Account'}
            </h3>
            <CardDescription className="text-xs">
              {mode === 'signin'
                ? 'Sign in to save and sync your diagrams'
                : 'Sign up to start creating diagrams'}
            </CardDescription>
          </CardHeader>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <CardContent className="p-6 space-y-4">
              <AnimatePresence mode="wait">
                {error && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.15 }}
                  >
                    <Alert variant="destructive">
                      <AlertDescription>{error}</AlertDescription>
                    </Alert>
                  </motion.div>
                )}
              </AnimatePresence>

              <AnimatePresence mode="wait">
                {mode === 'signup' && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.15 }}
                  >
                    <div className="space-y-2">
                      <Label htmlFor="name">Name</Label>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                          id="name"
                          type="text"
                          value={name}
                          onChange={(e) => setName(e.target.value)}
                          placeholder="Your name"
                          required
                          className="pl-10"
                        />
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    required
                    className="pl-10"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    required
                    minLength={8}
                    className="pl-10 pr-12"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-1 top-1/2 -translate-y-1/2 h-8 w-8 p-0 text-muted-foreground hover:text-foreground"
                  >
                    {showPassword ? (
                      <EyeOff className="w-4 h-4" />
                    ) : (
                      <Eye className="w-4 h-4" />
                    )}
                  </Button>
                </div>
                {mode === 'signin' && (
                  <div className="mt-2 text-right">
                    <Link
                      to="/forgot-password"
                      className="text-sm text-primary hover:text-primary/80 font-medium"
                    >
                      Forgot Password?
                    </Link>
                  </div>
                )}
              </div>

              <Button
                type="submit"
                disabled={isLoading || isGitHubLoading}
                className="w-full"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    {mode === 'signin' ? 'Signing in...' : 'Creating account...'}
                  </>
                ) : mode === 'signin' ? (
                  'Sign In'
                ) : (
                  'Create Account'
                )}
              </Button>

              <div className="relative my-4">
                <div className="absolute inset-0 flex items-center">
                  <Separator className="w-full" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-background px-2 text-muted-foreground">
                    Or continue with
                  </span>
                </div>
              </div>

              <Button
                type="button"
                variant="outline"
                onClick={handleGitHubSignIn}
                disabled={isLoading || isGitHubLoading}
                className="w-full"
              >
                {isGitHubLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    Connecting to GitHub...
                  </>
                ) : (
                  <>
                    <Github className="w-4 h-4 mr-2" />
                    Continue with GitHub
                  </>
                )}
              </Button>
            </CardContent>

            {/* Footer */}
            <CardFooter className="border-t border-border">
              <p className="text-sm text-muted-foreground mx-auto">
                {mode === 'signin' ? "Don't have an account? " : 'Already have an account? '}
                <Button
                  type="button"
                  variant="link"
                  onClick={switchMode}
                  className="p-0 h-auto font-semibold"
                >
                  {mode === 'signin' ? 'Sign up' : 'Sign in'}
                </Button>
              </p>
            </CardFooter>
          </form>
        </Card>
      </motion.div>

      {/* Theme toggle */}
      <button
        onClick={toggleTheme}
        className="fixed bottom-4 right-4 p-3 bg-card border border-border rounded-full shadow-lg hover:bg-muted transition-colors"
      >
        {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
      </button>
    </div>
  );
}

